import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, type Webhook, type WebhookDelivery } from '../api';
import { RefreshIcon } from '../components/icons';
import {
  CodeBlock,
  Copyable,
  Dialog,
  ErrorNote,
  formatDateTime,
  PageHeader,
  SkeletonRows,
  Status,
  timeAgo,
  useToast,
} from '../components/ui';
import { useLoad } from '../hooks';
import { useSession } from '../session';
import { EVENTS, SecretDialog, WebhookForm } from './Webhooks';

export function WebhookDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { team } = useSession();
  const canEdit = team.role !== 'member';
  const hook = useLoad(() => api<Webhook>('GET', `/webhooks/${encodeURIComponent(id)}`), [id]);
  const deliveries = useLoad(
    () =>
      api<{ data: WebhookDelivery[] }>(
        'GET',
        `/webhooks/${encodeURIComponent(id)}/deliveries?limit=50`,
      ),
    [id],
  );
  const [editing, setEditing] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'rotate' | 'delete' | null>(null);
  const [open, setOpen] = useState<WebhookDelivery | null>(null);
  const toast = useToast();

  if (hook.loading && !hook.data)
    return <div className="skeleton" style={{ height: 28, width: 260 }} />;
  if (hook.error || !hook.data) return <ErrorNote error={hook.error ?? 'Endpoint not found.'} />;
  const w = hook.data;
  const list = deliveries.data?.data ?? [];

  async function retry(delivery: WebhookDelivery) {
    try {
      await api(
        'POST',
        `/webhooks/${encodeURIComponent(w.id)}/deliveries/${encodeURIComponent(delivery.id)}/retry`,
      );
      toast.show('Queued: it will be sent again in a few seconds');
      setOpen(null);
      deliveries.reload();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not retry');
    }
  }

  return (
    <>
      <PageHeader
        crumbs={
          <>
            <Link to="/webhooks">Webhooks</Link>
            <span>/</span>
            <span className="mono">{w.id}</span>
          </>
        }
        title={
          <span style={{ fontSize: 22, fontFamily: 'var(--mono)', wordBreak: 'break-all' }}>
            {w.url}
          </span>
        }
        description={
          <>
            <Status status={w.status} />
            <span style={{ marginLeft: 10 }}>
              {w.status === 'disabled'
                ? 'Turned off. Vaartix is not sending to it.'
                : `${w.events.length === EVENTS.length ? 'All events' : w.events.join(', ')}`}
            </span>
          </>
        }
        actions={
          canEdit ? (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(true)}>
                Edit
              </button>
              <button
                type="button"
                className={w.status === 'enabled' ? 'btn btn-ghost' : 'btn btn-primary'}
                onClick={async () => {
                  await api('PATCH', `/webhooks/${encodeURIComponent(w.id)}`, {
                    status: w.status === 'enabled' ? 'disabled' : 'enabled',
                  });
                  hook.reload();
                }}
              >
                {w.status === 'enabled' ? 'Turn off' : 'Turn on'}
              </button>
            </>
          ) : null
        }
      />

      <div className="detail-grid">
        <div className="card" style={{ overflow: 'hidden' }}>
          <div className="card-head">
            <div>
              <h2>Deliveries</h2>
              <p>The last 50 requests to this endpoint, newest first.</p>
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={deliveries.reload}>
              <RefreshIcon />
              Refresh
            </button>
          </div>
          {!deliveries.loading && list.length === 0 ? (
            <div className="card-body muted">
              No deliveries yet. Send an email to <code>delivered@test.vaartix.com</code> to trigger
              one.
            </div>
          ) : (
            <div className="table-wrap" style={{ padding: '12px 12px 4px' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Status</th>
                    <th className="hide-sm">Response</th>
                    <th className="nowrap">When</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.loading ? (
                    <SkeletonRows cols={4} rows={3} />
                  ) : (
                    list.map((d) => (
                      <tr
                        key={d.id}
                        className="row-link"
                        tabIndex={0}
                        onClick={() => setOpen(d)}
                        onKeyDown={(e) => e.key === 'Enter' && setOpen(d)}
                      >
                        <td className="mono" style={{ fontSize: 13 }}>
                          {d.type}
                        </td>
                        <td>
                          <Status status={d.status} />
                          {d.attempts > 1 ? (
                            <span className="sub">{d.attempts} attempts</span>
                          ) : null}
                        </td>
                        <td className="hide-sm muted mono" style={{ fontSize: 13 }}>
                          {d.responseStatus ??
                            (d.status === 'pending' && d.attempts === 0 ? '—' : 'No response')}
                        </td>
                        <td className="nowrap muted">{timeAgo(d.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Signing secret</h2>
          </div>
          <div className="card-body">
            <p className="muted" style={{ marginTop: 0, fontSize: 13.5 }}>
              Shown once when created. If it may have leaked, rotate it: the old one stops working
              immediately.
            </p>
            {canEdit ? (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setConfirm('rotate')}
              >
                Rotate secret
              </button>
            ) : null}
            <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '18px 0' }} />
            <dl className="facts">
              <dt>ID</dt>
              <dd>
                <Copyable value={w.id} />
              </dd>
              <dt>Created</dt>
              <dd>{formatDateTime(w.createdAt)}</dd>
            </dl>
            {canEdit ? (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                style={{ marginTop: 18 }}
                onClick={() => setConfirm('delete')}
              >
                Delete endpoint
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {open ? (
        <Dialog
          wide
          title={open.type}
          description={`${formatDateTime(open.createdAt)} · ${open.attempts} attempt${open.attempts === 1 ? '' : 's'}`}
          onClose={() => setOpen(null)}
          footer={
            <>
              {canEdit && w.status === 'enabled' ? (
                <button type="button" className="btn btn-ghost" onClick={() => void retry(open)}>
                  Send again
                </button>
              ) : null}
              <button type="button" className="btn btn-primary" onClick={() => setOpen(null)}>
                Close
              </button>
            </>
          }
        >
          <div className="field">
            <span className="label">Request body</span>
            <CodeBlock code={JSON.stringify(open.payload, null, 2)} />
          </div>
          <div className="field">
            <span className="label">
              Your server's response{' '}
              {open.responseStatus ? (
                <Status
                  status={String(open.responseStatus)}
                  tone={open.status === 'succeeded' ? 'ok' : 'bad'}
                />
              ) : null}
            </span>
            <CodeBlock code={open.responseBody || '(empty)'} />
          </div>
          {open.status === 'pending' && open.nextAttemptAt ? (
            <p className="dim" style={{ margin: 0, fontSize: 13 }}>
              Next attempt {formatDateTime(open.nextAttemptAt)}.
            </p>
          ) : null}
        </Dialog>
      ) : null}

      {editing ? (
        <WebhookForm
          hook={w}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            hook.reload();
          }}
        />
      ) : null}
      {secret ? <SecretDialog secret={secret} onClose={() => setSecret(null)} /> : null}
      {confirm ? (
        <Dialog
          title={confirm === 'rotate' ? 'Rotate the signing secret?' : 'Delete this endpoint?'}
          description={
            confirm === 'rotate'
              ? 'Requests are signed with the new secret right away. Update your server first, or verification will fail.'
              : 'Vaartix stops sending to it immediately, and its delivery log is deleted.'
          }
          onClose={() => setConfirm(null)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  const action = confirm;
                  setConfirm(null);
                  try {
                    if (action === 'rotate') {
                      const res = await api<{ secret: string }>(
                        'POST',
                        `/webhooks/${encodeURIComponent(w.id)}/rotate-secret`,
                      );
                      setSecret(res.secret);
                    } else {
                      await api('DELETE', `/webhooks/${encodeURIComponent(w.id)}`);
                      navigate('/webhooks');
                    }
                  } catch (err) {
                    toast.show(err instanceof Error ? err.message : 'Something went wrong');
                  }
                }}
              >
                {confirm === 'rotate' ? 'Rotate secret' : 'Delete endpoint'}
              </button>
            </>
          }
        />
      ) : null}
      {toast.node}
    </>
  );
}
